import OnlineAppComponent from "../lib/online_app_component";
import {OnlineAppNavOption, OnlineAppNavOptionConfig} from "./online_app_nav_option";
import NavOptionCollection from "../lib/nav_option_collection";
import verge from "verge";
import {OnlineAppRootConfig} from "./online_app_root/online_app_root";
import {NavConfig} from "../nav/nav";
import {getThrottledFunction} from "../../helpers/throttle";

export const OnlineAppNavConfig = {
    componentTypeKey: 'OnlineAppNav',
    baseHtmlClass: 'online_app__main__nav',
    selector: '.online_app__main__nav',
    maxWidth: 350,

    constructedEventName: 'OnlineAppNav-constructed',
};

/**
 * Class representing a navigation component within the context of an Online App.
 * This is not the same as the `Nav` class from `app/javascript/rankers/online_apps/nav/nav.js`.
 * This class is a component that is primarily responsible for hiding/showing the navigation.
 * */
export class OnlineAppNav extends OnlineAppComponent {

    getNavOptionsElements() {
        return this.element.querySelectorAll(OnlineAppNavOptionConfig.selector);
    }

    getMapFilters() {
        return this.navOptionCollection.allWithAnyMapFilter().map(navOption => navOption.mapFilter);
    }

    getSubpages() {
        return this.navOptionCollection.allWithAnySubpage().map(navOption => navOption.subpage);
    }

    constructor(id_or_element, onlineApp) {
        super(id_or_element, onlineApp, OnlineAppNavConfig);

        /**
         * Whether this OnlineAppNav component is attached or not.
         * This "attachment" relates to whether the element should be included in the layers that are rendered by the browser's rendering engine.
         * Essentially when not attached the element should be `display: none;` (or the element could be removed from the DOM) - this greatly improves rendering performance and memory usage.
         * Whenever the nav is toggled we toggle the `isAttached` state and render it.
         *
         * @name OnlineAppNav#isAttached
         * @type Boolean
         * @default false
         */
        this.isAttached = false;

        this.navOptionCollection = new NavOptionCollection();
        this.getNavOptionsElements().forEach((element) => {
            this.navOptionCollection.push(new OnlineAppNavOption(element, this.onlineApp, this));
        });

        // Reflect the Nav webview's authoritative open/close (plan 2026-07-15). The Nav is the sole
        // owner of nav open/closed state; we are a reflector — we mirror OnlineAppNav-open/-close
        // into the online_app's navOpen state and attach/detach the nav element for render
        // performance. We never own or toggle nav state ourselves.
        this.onlineApp.doc.addEventListener(NavConfig.openEventName, this.handleReflectOpen.bind(this));
        this.onlineApp.doc.addEventListener(NavConfig.closeEventName, this.handleReflectClose.bind(this));

        const resizeHandler = getThrottledFunction(this.handleResize.bind(this), 100);
        this.observer = new ResizeObserver(resizeHandler);
        this.observer.observe(this.onlineApp.doc.documentElement);
    }

    // The Nav has opened: add the element to the render layers (if it was detached) and mirror
    // the open state. Rendering (slide-in) follows from the navOpen state change.
    handleReflectOpen() {
        if (!this.isOpen()) this.attach();
        this.dispatchAction('navOpenReflected', true);
    }

    // The Nav has closed: detach the element from the render layers (after the slide-out) and
    // mirror the closed state.
    handleReflectClose() {
        if (this.isOpen()) this.detach();
        this.dispatchAction('navOpenReflected', false);
    }

    /**
     * Detach the component from the browser rendering layers.
     */
    detach() {
        this.isAttached = false;
        this.renderAttached();
    }

    /**
     * Attach the component to the browser rendering layers.
     */
    attach() {
        this.isAttached = true;
        this.renderAttached();
    }

    /**
     * Renders the current state of `isAttached` to the DOM.
     * When not attached we have a delay so that any animations can complete before the element is hidden.
     */
    renderAttached() {
        if (this.isAttached) {
            this.removeHtmlClassState('detached');
        } else {
            setTimeout(() => {
                if (!this.isAttached) { // Check again in case the state has changed within the delay.
                    this.addHtmlClassState('detached');
                } else {
                    this.removeHtmlClassState('detached');
                }
            }, 1000); // Delay the rendering so that any animations can complete.
        }
    }

    handleResize(event) {
        this.renderOpenState(this.readState('navOpen'));
    }

    setWidth() {
        const modeCode = this.readState('modal');
        if (modeCode === OnlineAppRootConfig.modalModeCode) {
            this.width = `${verge.viewportW()}px`;
        } else {
            this.width = `${this.getMaxWidth()}px`;
        }
    }

    getMaxWidth() {
        return OnlineAppNavConfig.maxWidth;
    }

    renderWidth() {
        if (this.width) {
            this.element.style.width = this.width;
        } else {
            this.element.style.width = null;
        }
    }

    renderOpen() {
        this.addHtmlClassState('open');
        this.removeHtmlClassState('closed');
        this.element.style.left = null;
    }

    renderClosed() {
        this.addHtmlClassState('closed');
        this.removeHtmlClassState('open');
        this.element.style.left = `-${this.width}`;
    }

    renderOpenState(state) {
        this.setWidth();
        this.renderWidth();
        state ? this.renderOpen() : this.renderClosed();
    }

    render() {
        this.renderOpenState(this.isOpen());
    }

    isOpen() {
        return this.readState('navOpen');
    }

    // Get the horizontal space that the navigation takes up. This will be zero when the nav is closed.
    getHorizSpace() {
        const space = this.element.offsetWidth + this.element.offsetLeft;
        return space > 0 ? space : 0;
    }

}
